import { useBack } from "@website/hooks/back";
import { MaterialIcon } from "@website/icons/material";
import { Button } from "@website/ui/button";
import { InfoChips } from "@website/ui/info-chips";
import { ListItem } from "@website/ui/list-item";
import { Modal } from "@website/ui/modal";
import { Popover } from "@website/ui/popover";
import { isChannel } from "@website/utils/channel/helpers";
import { numberConvert } from "@website/utils/number-convert";
import { PurchaseMenu } from "@website/widget/common/components/purchase-menu";
import { usePurchaseMenu } from "@website/widget/common/utils/use-purchase-menu";
import styles from "@website/widget/header-jek/header-jek.module.scss";
import { HeaderJekPropertiesType } from "@website/widget/header-jek/header-jek.types";
import { useLogin, useSupportMenu } from "@website/widget/header-jek/hooks";
import { Login, useLoginModal } from "@website/widget/login";
import { FC, memo } from "react";
import { useSnappPro } from "../header/hooks/use-snapp-pro";

const HeaderJek: FC<HeaderJekPropertiesType> = (properties) => {
  const {
    showShareButton = false,
    showSupportButton = false,
    showPurchaseMenu = false,
    showBackButton = true,
    title = "سفر"
  } = properties;
  const { showAuth, onProfileClick } = useLogin();
  const {
    purchaseList,
    purchaseMenuIsOpen,
    onPurchaseMenuClose,
    onPurchaseMenuOpen
  } = usePurchaseMenu();
  const { onSupportMenuOpen, onSupportMenuClose, supportMenuIsOpen } =
    useSupportMenu();
  const { goBack } = useBack();
  const { loginModalProps, onLoginModalClose, onLoginModalEnd } =
    useLoginModal();
  const { hasSnappPro, snappProIcon } = useSnappPro();

  return (
    <>
      <div
        className={`relative z-30 flex h-16 w-full items-center justify-between bg-white px-4 ${properties.className}`}
      >
        <div className="flex flex-row items-center gap-2">
          {showBackButton && (
            <Button
              EndIcon={
                <MaterialIcon
                  className="text-on-surface-medium-emphasis"
                  name="arrow_forward"
                  variant="outlined"
                />
              }
              aria-label="close"
              color="neutral"
              equalDimension
              onClick={goBack}
              size="small"
              variant="text"
            />
          )}
          <h6 className="text-headline-6 font-normal">{title}</h6>
        </div>
        <div className="flex flex-row items-center justify-end gap-4">
          {showShareButton && (
            <Button
              EndIcon={
                <MaterialIcon
                  className="text-on-surface-medium-emphasis"
                  name="share"
                  variant="outlined"
                />
              }
              aria-label="share"
              color="neutral"
              equalDimension
              size="small"
              variant="text"
            />
          )}

          {showSupportButton && (
            <div>
              <Button
                EndIcon={
                  <MaterialIcon
                    className="text-on-surface-medium-emphasis"
                    name="support_agent"
                  />
                }
                aria-label="نمایش لینک های پشتیبانی"
                color="neutral"
                equalDimension
                onClick={onSupportMenuOpen}
                role="button"
                size="medium"
                variant="text"
              />

              <Popover
                gap="16px"
                horizontalPosition="end"
                onClose={onSupportMenuClose}
                open={supportMenuIsOpen}
              >
                <div>
                  {isChannel(["website", "web"]) && (
                    <ListItem
                      StartIcon={<MaterialIcon name="help_outline" />}
                      aria-label="سوالات متداول"
                      href="/"
                      role="link"
                      target="_blank"
                      title="سوالات متداول"
                    />
                  )}

                  <ListItem
                    StartIcon={<MaterialIcon name="support_agent" />}
                    aria-label="تماس با پشتیبانی"
                    href="tel:+982192000123"
                    role="link"
                    subtitle={
                      <div className="text-end" dir="ltr">
                        {numberConvert("+98 21 92000123", {
                          locale: "fa"
                        })}
                      </div>
                    }
                    title="تماس با پشتیبانی"
                  />
                </div>
              </Popover>
            </div>
          )}
          {showPurchaseMenu && (
            <div className={styles["purchase-wrapper"]}>
              <InfoChips
                size="compact"
                variant="round-contained"
                text="سفرهای من"
                className="!absolute -right-10 -top-0 !w-max lg:!hidden"
              />
              <Button
                EndIcon={<MaterialIcon name="luggage" variant="outlined" />}
                aria-label="Show Purchase List"
                color="neutral"
                equalDimension
                onClick={onPurchaseMenuOpen}
                variant="text"
              />
              <PurchaseMenu
                isOpen={purchaseMenuIsOpen}
                list={purchaseList}
                onClose={onPurchaseMenuClose}
              />
            </div>
          )}
          {showAuth && (
            <div
              className={`relative ${
                hasSnappPro
                  ? "border-on-surface-high-emphasis !rounded-full border"
                  : ""
              }`}
            >
              <Button
                EndIcon={
                  <MaterialIcon name="person_outline" variant="outlined" />
                }
                aria-label="share"
                className="!m-0"
                color="neutral"
                equalDimension
                onClick={onProfileClick}
                role="button"
                size="small"
                target="_blank"
                variant="text"
              />
              {hasSnappPro && (
                <img
                  className="absolute -bottom-3 right-[6px]"
                  src={snappProIcon}
                  alt="snapp-pro-icon"
                />
              )}
            </div>
          )}
        </div>
      </div>
      <Modal className="!z-50" {...loginModalProps} usePortal>
        <Login onClose={onLoginModalClose} onEndAuth={onLoginModalEnd} />
      </Modal>
    </>
  );
};

const HeaderJekMemoized = memo(HeaderJek);
export { HeaderJekMemoized as HeaderJek };
